/**
 * useUserProfileModals Hook
 * Manages modal state for User Profile component using consolidated state
 */

import { useState, useCallback } from "react";

// Initial modal state
const initialModalState = {
  imageCrop: false,
  confirmation: false,
};

export const useUserProfileModals = () => {
  // Consolidated modal state
  const [modals, setModals] = useState(initialModalState);
  
  // Individual modal states (for backward compatibility)
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Sync individual states with consolidated state
  const updateModals = useCallback((updates) => {
    setModals((prev) => {
      const newState = { ...prev, ...updates };
      
      // Sync individual states
      setModalIsOpen(newState.imageCrop);
      setShowConfirmation(newState.confirmation);
      
      return newState;
    });
  }, []);

  // Consolidated modal handlers
  const openImageCropModal = useCallback(() => {
    updateModals({ imageCrop: true });
  }, [updateModals]);

  const closeImageCropModal = useCallback(() => {
    updateModals({ imageCrop: false });
  }, [updateModals]);

  const openConfirmationModal = useCallback(() => {
    updateModals({ confirmation: true });
  }, [updateModals]);

  const closeConfirmationModal = useCallback(() => {
    updateModals({ confirmation: false });
  }, [updateModals]);

  const closeAllModals = useCallback(() => {
    setModals(initialModalState);
    setModalIsOpen(false);
    setShowConfirmation(false);
  }, []);

  return {
    // Consolidated modal state
    modals,
    
    // Individual modal states (for backward compatibility)
    modalIsOpen,
    showConfirmation,
    showSuccessMessage,
    
    // Open handlers
    openImageCropModal,
    openConfirmationModal,
    
    // Close handlers
    closeImageCropModal,
    closeConfirmationModal,
    closeAllModals,
    
    // Direct setters (for backward compatibility)
    setModalIsOpen,
    setShowConfirmation,
    setShowSuccessMessage,
    
    // Consolidated state setter
    setModals,
  };
};
